<template>
    <div :class="computedClass" class="min-h-screen relative">
        <!-- Header -->
        <div class="relative z-50">
            <header
                class="bg-white/40 backdrop-blur-md fixed top-0 left-0 w-full p-4"
            >
                <div class="container mx-auto flex justify-between">
                    <NuxtLink
                        :to="$localeRoute('/')"
                        class="font-bold flex items-center"
                    >
                        <img
                            :src="logoPng"
                            alt="Logo"
                            class="h-4 md:h-6 lg:h-6 z-auto"
                        />
                    </NuxtLink>
                    <LanguageSelector
                        class="flex gap-4 items-center mt-3"
                        :languages="languages"
                    />
                </div>
            </header>
        </div>

        <!-- Content with padding -->
        <transition name="fade" mode="out-in">
            <div
                class="container mx-auto my-auto p-4 pt-32"
                :key="route.fullPath"
            >
                <div :class="patternClass"></div>
                <slot />
                <div
                    v-if="updateAvailable"
                    class="fixed bottom-4 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-bounce"
                >
                    <span>New version available.</span>
                    <button
                        class="ml-4 underline font-semibold"
                        @click="refreshPage"
                    >
                        Refresh
                    </button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { useLanguagesStore } from "~/stores/languages";
import logo from "~/assets/images/logo.png";

const { setLocale } = useI18n();
const route = useRoute();
const languageStore = useLanguagesStore();
languageStore.initializeLanguage();

// Dynamically compute classes for background based on the current route
const computedClass = computed(() => {
    return {
        "bg-gradient-to-tr from-orange-200 to-transparent":
            route.path.indexOf("/about") !== -1 ||
            route.path.indexOf("/contact") !== -1,
        "bg-gradient-to-tr from-violet-200 to-transparent":
            route.path.indexOf("/patients") !== -1 &&
            !(route.path.indexOf("/patients/prevention") !== -1) &&
            !(route.path.indexOf("/patients/packages") !== -1),
        "bg-gradient-to-tr from-blue-200 to-transparent":
            route.path.indexOf("/scientists") !== -1,
        "bg-gradient-to-tr from-lime-200 to-transparent":
            route.path.indexOf("/patients/prevention") !== -1 ||
            route.path.indexOf("/patients/packages") !== -1,
    };
});

const patternClass = computed(() => {
    return {
        "absolute inset-0 -z-50 pattern-cross pattern-orange-600 pattern-bg-white pattern-size-16 pattern-opacity-20":
            route.path.indexOf("/about") !== -1 ||
            route.path.indexOf("/contact") !== -1,
        "absolute inset-0 -z-50 pattern-cross pattern-violet-600 pattern-bg-white pattern-size-16 pattern-opacity-20":
            route.path.indexOf("/patients") !== -1 &&
            !(route.path.indexOf("/patients/prevention") !== -1),
        "absolute inset-0 -z-50 pattern-cross pattern-blue-600 pattern-bg-white pattern-size-16 pattern-opacity-20":
            route.path.indexOf("/scientists") !== -1,
        "absolute inset-0 -z-50 pattern-cross pattern-lime-600 pattern-bg-white pattern-size-16 pattern-opacity-20":
            route.path.indexOf("/patients/prevention") !== -1,
    };
});

const languages = [
    { code: "en", name: "English" },
    // { code: "sr", name: "Srpski" },
    { code: "no", name: "Norsk" },
];

const logoPng = logo;
const { updateAvailable } = useUpdateAvailable();

const refreshPage = () => {
    if (process.client) {
        window.location.reload();
    }
};
</script>

<style scoped>
/* Define the fade transition */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.crossee-violet {
    background:
        radial-gradient(circle, rgba(186, 160, 211, 1), rgba(255, 255, 255, 0)),
        /* Pattern with crosses */
            url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><rect width="40" height="40" fill="none"/><line x1="20" y1="10" x2="20" y2="30" stroke="white" stroke-width="2"/><line x1="10" y1="20" x2="30" y2="20" stroke="white" stroke-width="2"/></svg>');

    /* Adjust background properties */
    background-size:
        cover,
        40px 40px;
}
</style>
